/**
 * The record forms' words: one table, per projection, of every word a
 * record dialog and its card share. A later form appends its rows here
 * rather than writing words into a component.
 *
 *   - Titles name the kind of thing ("Add condition", "Edit condition"),
 *     never a row's value; the primary names the outcome ("Add
 *     condition", "Save condition"), never a bare Save or Add.
 *   - A field's label is the card's label for the same fact; a field
 *     with no row reads fieldLabel. A help line sits under some boxes.
 *   - Every select opens on a real answer: a registry default, a prompt
 *     for a required field ("Choose a risk"), or an optional field's own
 *     empty state ("Not stated").
 *   - Stored tokens read in plain words, per field, so an ordinary word
 *     elsewhere ("discharge", "none") keeps its own reading.
 *   - Two rules the dialog applies before anything is sent: which fields
 *     show for a value (Named person, with the instrument) and which a
 *     value requires (an appointment's date, with its status); and the
 *     dates that are past by nature.
 */

import { CONTACT_ROLE_KEYS, checkboxFieldLabel, fieldLabel, humanizeToken } from '@/lib/fieldLabels';
import {
  CODE_STATUS_INSTRUMENTS,
  COVERAGE_PROGRAMS,
  LEGAL_INSTRUMENTS,
  PROPOSAL_PAYLOAD_SPECS,
} from '@/lib/noteVocabulary';
import { RECORD_ADD_PROJECTIONS, type RecordAddProjection } from '@/lib/clientRecordEditApi';

/** A record form's words key: every add projection, plus 'housing', the
 *  housing note's confirm card. Housing has no add or edit form since
 *  22l1 (the type of place is on the address), but its note's card still
 *  reads its labels, openings and value words here. */
export type RecordFormKey = RecordAddProjection | 'housing';
import type { NameCaseKind } from '@/lib/valueCase';

/** An insurance card's fields beyond the company, in the form's order. */
export const COVERAGE_CARD_FIELDS = [
  'plan_name',
  'member_id',
  'group_number',
  'card_phone',
  'rx_bin',
  'rx_pcn',
  'rx_group',
  'holder_name',
  'holder_relationship',
] as const;

/** Which of the card's fields each program's card carries. A browser
 *  rule only: the server stores any of them for any program. Original
 *  Medicare shows its number; a plan with drug cover shows the Rx lines;
 *  a private or other policy may be in someone else's name. */
export const COVERAGE_CARD_BY_PROGRAM: Readonly<Record<string, readonly string[]>> = {
  medicare_a: ['member_id'],
  medicare_b: ['member_id'],
  medicare_a_b: ['member_id'],
  medicare_advantage: ['plan_name', 'member_id', 'group_number', 'card_phone', 'rx_bin', 'rx_pcn', 'rx_group'],
  part_d: ['plan_name', 'member_id', 'card_phone', 'rx_bin', 'rx_pcn', 'rx_group'],
  supplement: ['plan_name', 'member_id', 'group_number', 'card_phone'],
  ltc_insurance: ['plan_name', 'member_id', 'group_number', 'card_phone'],
  medicaid: ['plan_name', 'member_id', 'card_phone'],
  private: [...COVERAGE_CARD_FIELDS],
  other: [...COVERAGE_CARD_FIELDS],
};

export type RecordDialogMode = 'create' | 'edit';

export interface RecordDialogWords {
  addTitle: string;
  addPrimary: string;
  /** Absent where the projection has no edit route. */
  editTitle?: string;
  editPrimary?: string;
}

// ---------------------------------------------------------------------
// Titles and primaries
// ---------------------------------------------------------------------

export const RECORD_DIALOG_WORDS: Readonly<Record<RecordAddProjection, RecordDialogWords>> = {
  medications: { addTitle: 'Add medication', addPrimary: 'Add medication', editTitle: 'Edit medication', editPrimary: 'Save medication' },
  conditions: { addTitle: 'Add condition', addPrimary: 'Add condition', editTitle: 'Edit condition', editPrimary: 'Save condition' },
  allergies: { addTitle: 'Add allergy', addPrimary: 'Add allergy', editTitle: 'Edit allergy', editPrimary: 'Save allergy' },
  appointments: { addTitle: 'Add appointment', addPrimary: 'Add appointment', editTitle: 'Edit appointment', editPrimary: 'Save appointment' },
  'risk-flags': { addTitle: 'Add risk flag', addPrimary: 'Add risk flag', editTitle: 'Edit risk flag', editPrimary: 'Save risk flag' },
  'functional-status': {
    addTitle: 'Add functional status',
    addPrimary: 'Add to functional status',
    editTitle: 'Edit functional status',
    editPrimary: 'Save functional status',
  },
  'hospital-events': {
    addTitle: 'Add hospital or rehab stay',
    addPrimary: 'Add to hospital and rehab',
    editTitle: 'Edit hospital or rehab stay',
    editPrimary: 'Save stay',
  },
  'legal-instruments': {
    addTitle: 'Add to Legal & wishes',
    addPrimary: 'Add to Legal & wishes',
    editTitle: 'Edit Legal & wishes',
    editPrimary: 'Save changes',
  },
  coverage: { addTitle: 'Add insurance', addPrimary: 'Add insurance', editTitle: 'Edit insurance', editPrimary: 'Save insurance' },
  contacts: { addTitle: 'Add contact', addPrimary: 'Add contact', editTitle: 'Edit contact', editPrimary: 'Save contact' },
  providers: { addTitle: 'Add to the care team', addPrimary: 'Add to care team', editTitle: 'Edit care provider', editPrimary: 'Save care provider' },
};

/** The dialog's title for a projection and mode. */
export function recordDialogTitle(projection: RecordAddProjection, mode: RecordDialogMode): string {
  const words = RECORD_DIALOG_WORDS[projection];
  return mode === 'create' ? words.addTitle : (words.editTitle ?? words.addTitle);
}

/** The primary button's words for a projection and mode. */
export function recordPrimaryLabel(projection: RecordAddProjection, mode: RecordDialogMode): string {
  const words = RECORD_DIALOG_WORDS[projection];
  return mode === 'create' ? words.addPrimary : (words.editPrimary ?? words.addPrimary);
}

// ---------------------------------------------------------------------
// Field labels, help lines and field order
// ---------------------------------------------------------------------

type PerField<T> = Readonly<Partial<Record<RecordFormKey, Readonly<Record<string, T>>>>>;

export const RECORD_FIELD_LABELS: PerField<string> = {
  medications: {
    name: 'Medication name',
    dose: 'Dose',
    times_of_day: 'When',
    schedule: 'Directions',
    status: 'Status',
    // LP-41: either date may be a day still to come, so the words name
    // the date, not a past event.
    started_on: 'Start date',
    ended_on: 'Stop date',
    prescriber: 'Prescriber',
    reason: 'Reason or condition',
  },
  allergies: { substance: 'Allergy' },
  conditions: { name: 'Condition', noted_on: 'Noted' },
  'risk-flags': { category: 'Risk', basis: 'What shows this' },
  'functional-status': { note: 'Note' },
  'hospital-events': { kind: 'What happened', event_on: 'Date', facility: 'Hospital or rehab' },
  // The place's name line and its address group in the one Where block
  // (AppointmentWhere).
  appointments: { scheduled_time: 'Time', recurrence: 'Repeats', outcome: 'Notes', location_name: 'Location', location_address: 'Address' },
  'legal-instruments': {
    instrument: 'Document or wish',
    code_status_values: 'Code status',
    designee: 'Named person',
    effective_on: 'Effective',
  },
  coverage: {
    provider: 'Insurance company',
    plan_name: 'Plan',
    member_id: 'Member ID',
    group_number: 'Group number',
    card_phone: 'Phone on the card',
    rx_bin: 'Rx BIN',
    rx_pcn: 'Rx PCN',
    rx_group: 'Rx group',
    holder_name: 'Card holder',
    holder_relationship: 'Relationship to the client',
    detail: 'Notes',
  },
};

/** The label a record form and its card show for a field. */
export function recordFieldLabel(projection: RecordFormKey, field: string): string {
  // A contact's roles read in the one map's words (checkboxFieldLabel).
  if (projection === 'contacts' && (CONTACT_ROLE_KEYS as readonly string[]).includes(field)) return checkboxFieldLabel(field);
  return RECORD_FIELD_LABELS[projection]?.[field] ?? fieldLabel(field);
}

export const RECORD_FIELD_HELP: PerField<string> = {
  medications: {
    schedule: 'Anything else about taking it, for example with food, or every other day.',
    // LP-41: a later day is welcome and lands the row in its place at once.
    started_on: 'A later day lists it under Starting soon until then.',
    ended_on: 'The day it stops. Until then it stays current.',
  },
  allergies: { substance: 'As the doctor or the label writes it.' },
  'hospital-events': { facility: 'Pick it from the care team or the Directory, or type its name.' },
  'risk-flags': { basis: 'What you saw or were told.' },
  contacts: { relationship: 'For example Daughter, Neighbor, Attorney.' },
  'functional-status': { note: 'What shows it, for example “needs standby help in the shower”.' },
  'legal-instruments': { designee: 'The agent, proxy, guardian, trustee or executor, if there is one.' },
};

/** The help line under a field's box, or undefined. */
export function recordFieldHelp(projection: RecordFormKey, field: string): string | undefined {
  return RECORD_FIELD_HELP[projection]?.[field];
}

/** The order a form draws its fields in where it differs from the
 *  registry's. */
export const RECORD_FIELD_ORDER: Readonly<Partial<Record<RecordFormKey, readonly string[]>>> = {
  medications: ['name', 'dose', 'times_of_day', 'schedule', 'status', 'started_on', 'ended_on', 'prescriber', 'reason'],
  'hospital-events': ['kind', 'event_on', 'facility', 'note'],
  coverage: [
    'program',
    'provider',
    ...COVERAGE_CARD_FIELDS,
    'status',
    'verified_on',
    'detail',
  ],
};

/** Fields that span both of the form's columns, besides a box of many
 *  lines and a set of ticks. */
export const RECORD_WIDE_FIELDS: Readonly<Partial<Record<RecordFormKey, readonly string[]>>> = {
  medications: ['schedule', 'prescriber', 'reason'],
};

/** True when the field spans both of the form's columns by name. */
export function isWideField(projection: RecordFormKey, field: string): boolean {
  return RECORD_WIDE_FIELDS[projection]?.includes(field) ?? false;
}

/**
 * How a set of ticks is drawn, keyed `projection.field`. `code_status` is
 * the code-status widget (the three that combine, a line, the three that
 * stand alone); an emptied code-status set is sent as its own token.
 * `ticks` is one wrapping row of labelled checkboxes. Every enum_set the
 * record dialog can draw has a row.
 */
export type TickArrangement = 'code_status' | 'ticks';

export const RECORD_TICK_GROUPS: Readonly<Record<string, { arrangement: TickArrangement }>> = {
  'legal-instruments.code_status_values': { arrangement: 'code_status' },
  'medications.times_of_day': { arrangement: 'ticks' },
};

/** A set's arrangement; a set with no row is drawn as ticks. */
export function tickArrangement(projection: RecordFormKey, field: string): TickArrangement {
  return RECORD_TICK_GROUPS[`${projection}.${field}`]?.arrangement ?? 'ticks';
}

// ---------------------------------------------------------------------
// How a select opens
// ---------------------------------------------------------------------

/** The words of a select's first option (value ''): a prompt for a
 *  required field, an optional field's empty state. A field with a
 *  registry default has no row here: it opens on the default. */
export const RECORD_SELECT_OPENINGS: PerField<string> = {
  allergies: { severity: 'Not stated' },
  'risk-flags': { category: 'Choose a risk', level: 'Choose a level' },
  'legal-instruments': { instrument: 'Choose one', status: 'Choose a status' },
  coverage: { program: 'Choose a program' },
  'functional-status': { activity: 'Choose an activity', level: 'Choose a level' },
  'hospital-events': { kind: 'Choose what happened' },
  housing: { living_situation: 'Choose a living situation' },
};

/** The first option's words, or undefined where a default opens. */
export function selectOpening(projection: RecordFormKey, field: string): string | undefined {
  return RECORD_SELECT_OPENINGS[projection]?.[field];
}

// ---------------------------------------------------------------------
// Plain words for stored tokens
// ---------------------------------------------------------------------

export const RECORD_VALUE_LABELS: PerField<Readonly<Record<string, string>>> = {
  medications: {
    times_of_day: {
      morning: 'Morning',
      afternoon: 'Afternoon',
      evening: 'Evening',
      bedtime: 'Bedtime',
      as_needed: 'As needed (PRN)',
    },
  },
  'functional-status': {
    activity: {
      dressing_upper: 'Dressing, upper body',
      dressing_lower: 'Dressing, lower body',
      transfer_chair: 'Getting in and out of a chair',
      transfer_bed: 'Getting in and out of bed',
      ambulation: 'Walking',
      feeding: 'Eating',
      meal_prep: 'Preparing meals',
      medication_setup: 'Setting up medications',
      medication_reminders: 'Remembering medications',
      finances: 'Money and bills',
      telephone: 'Using the phone',
    },
  },
  'hospital-events': {
    kind: {
      admission: 'Admitted to hospital',
      discharge: 'Discharged from hospital',
      rehab_admission: 'Admitted to rehab',
      rehab_discharge: 'Discharged from rehab',
    },
  },
  appointments: {
    recurrence: { none: 'Does not repeat' },
  },
  'legal-instruments': {
    code_status_values: {
      dnr: 'DNR (do not resuscitate)',
      dni: 'DNI (do not intubate)',
      dnh: 'DNH (do not hospitalize)',
      full: 'Full code',
      comfort: 'Comfort care',
      unknown: 'Not recorded',
    },
  },
};

/** The word a stored token reads as in one field of one form; a token
 *  with no row reads humanizeToken. */
export function valueLabel(projection: RecordFormKey, field: string, token: string): string {
  return RECORD_VALUE_LABELS[projection]?.[field]?.[token] ?? humanizeToken(token);
}

/** The record form a proposal type is confirmed into: the projection
 *  whose registry spec the type shares (both appointment types are the
 *  appointments form; the three medication types the medications form).
 *  The one reader of "which form is this card"; undefined for a type
 *  with no registry spec (the SPICE proposals). */
export function recordProjectionForType(proposalType: string): RecordFormKey | undefined {
  // The housing note's card keeps its words with no add form behind it.
  if (proposalType === 'housing_change') return 'housing';
  const spec = (PROPOSAL_PAYLOAD_SPECS as Record<string, unknown>)[proposalType];
  if (spec === undefined) return undefined;
  return (Object.keys(RECORD_ADD_PROJECTIONS) as RecordAddProjection[]).find(
    (p) => PROPOSAL_PAYLOAD_SPECS[RECORD_ADD_PROJECTIONS[p].specType] === spec,
  );
}

/** The same map keyed by proposal type, through recordProjectionForType. */
export function valueLabelForType(proposalType: string, field: string, token: string): string {
  const projection = recordProjectionForType(proposalType);
  return projection === undefined ? humanizeToken(token) : valueLabel(projection, field, token);
}

/** The line between the three code-status ticks that combine and the
 *  three that stand alone. */
export const CODE_STATUS_GROUP_LINE = 'These three can go together. Or choose one of:';

// ---------------------------------------------------------------------
// Rules the dialog applies before anything is sent
// ---------------------------------------------------------------------

interface ValueRule {
  field: string;
  values: readonly string[];
  /** RECORD_BROWSER_APPLIES only. On an edit, a value the dialog drew when it opened and hides at save is sent as null. */
  clearWhenHidden?: true;
  /** RECORD_BROWSER_APPLIES only. As clearWhenHidden, for a field that
   *  cannot be null: the hidden value is sent as this token instead. */
  resetWhenHidden?: string;
  /** RECORD_BROWSER_APPLIES only. The confirm card's rule alone: the
   *  record form always draws the field, so never hides or clears it. */
  confirmCardOnly?: true;
}

type PerFieldRule = Readonly<Partial<Record<RecordFormKey, Readonly<Record<string, ValueRule>>>>>;

function ruleHolds(rule: ValueRule | undefined, read: (field: string) => unknown): boolean {
  return rule !== undefined && rule.values.includes(String(read(rule.field)));
}

/** Fields the browser shows only while another field's value is one of
 *  the rule's: Named person, for an instrument that carries no code
 *  status; a medication's Stop date on the confirm card, while it is
 *  discontinued. The record form draws Stop date at every status (LP-41):
 *  an active medication may stop on a later day. The confirm card is the
 *  note pipeline's, which LP-41 leaves as it was. The registry's own
 *  appliesWhen is untouched by these. */
export const RECORD_BROWSER_APPLIES: PerFieldRule = {
  medications: {
    ended_on: { field: 'status', values: ['discontinued'], clearWhenHidden: true, confirmCardOnly: true },
  },
  // One date follows the status; the cadence for a visit still to come.
  // Neither date is ever cleared. The outcome is the record form's Notes,
  // drawn at every status; the confirm card shows the reader's outcome
  // only for a visit that happened.
  appointments: {
    scheduled_for: { field: 'status', values: ['scheduled', 'cancelled'] },
    // 0218: the time sits beside a date still to come.
    scheduled_time: { field: 'status', values: ['scheduled'], clearWhenHidden: true },
    occurred_on: { field: 'status', values: ['attended', 'missed'] },
    outcome: { field: 'status', values: ['attended'], clearWhenHidden: true, confirmCardOnly: true },
    recurrence: { field: 'status', values: ['scheduled'], resetWhenHidden: 'none' },
  },
  'legal-instruments': {
    designee: {
      field: 'instrument',
      values: LEGAL_INSTRUMENTS.filter((i) => !CODE_STATUS_INSTRUMENTS.includes(i)),
    },
  },
  coverage: Object.fromEntries(
    COVERAGE_CARD_FIELDS.map((field) => [
      field,
      {
        field: 'program',
        values: COVERAGE_PROGRAMS.filter((program) => COVERAGE_CARD_BY_PROGRAM[program]?.includes(field) ?? false),
        clearWhenHidden: true,
      },
    ]),
  ),
};

/** True when the dialog draws, counts and sends the field among the
 *  values `read` gives: always for a field with no rule here. */
export function browserApplies(
  projection: RecordFormKey,
  field: string,
  read: (field: string) => unknown,
): boolean {
  const rule = RECORD_BROWSER_APPLIES[projection]?.[field];
  return rule === undefined || ruleHolds(rule, read);
}

/** As browserApplies, for the record form: a confirm-card-only rule
 *  does not apply there. */
export function recordFormApplies(
  projection: RecordFormKey,
  field: string,
  read: (field: string) => unknown,
): boolean {
  const rule = RECORD_BROWSER_APPLIES[projection]?.[field];
  return rule === undefined || rule.confirmCardOnly === true || ruleHolds(rule, read);
}

/** What an edit that hides the field sends in place of its stored
 *  value: null (clearWhenHidden), the rule's token (resetWhenHidden), or
 *  undefined when the stored value stays as it is. */
export function browserValueWhenHidden(projection: RecordFormKey, field: string): null | string | undefined {
  const rule = RECORD_BROWSER_APPLIES[projection]?.[field];
  if (rule?.clearWhenHidden === true) return null;
  return rule?.resetWhenHidden;
}

/** Dates that are past by nature: the box caps at the practice's today
 *  and a later date is a gap. */
export const RECORD_PAST_DATES: Readonly<Partial<Record<RecordFormKey, readonly string[]>>> = {
  medications: ['started_on', 'ended_on'],
  conditions: ['noted_on'],
  'hospital-events': ['event_on'],
};

export function isPastByNature(projection: RecordFormKey, field: string): boolean {
  return RECORD_PAST_DATES[projection]?.includes(field) ?? false;
}

/** Past-by-nature dates the record form takes on a later day all the same
 *  (LP-41): a medication the client starts, or stops, in a few weeks. The
 *  confirm card (the note pipeline's, unchanged by LP-41) still caps them. */
const RECORD_FORM_LATER_DATES: Readonly<Partial<Record<RecordFormKey, readonly string[]>>> = {
  medications: ['started_on', 'ended_on'],
};

/** True when the record form caps the date at today and a later one is a
 *  gap: past by nature, and not one the form takes on a later day. */
export function isPastOnRecordForm(projection: RecordFormKey, field: string): boolean {
  return isPastByNature(projection, field) && !(RECORD_FORM_LATER_DATES[projection]?.includes(field) ?? false);
}

/** Fields another field's value requires: an appointment's Scheduled for
 *  while its status is Scheduled, its Occurred on while Attended or
 *  Missed. The writer applies the same pairing. */
export const RECORD_REQUIRED_WHEN: PerFieldRule = {
  appointments: {
    scheduled_for: { field: 'status', values: ['scheduled'] },
    occurred_on: { field: 'status', values: ['attended', 'missed'] },
  },
};

/** True when the values `read` gives make the field required. */
export function requiredWhen(
  projection: RecordFormKey,
  field: string,
  read: (field: string) => unknown,
): boolean {
  return ruleHolds(RECORD_REQUIRED_WHEN[projection]?.[field], read);
}

// ---------------------------------------------------------------------
// Fields that name someone on the care team
// ---------------------------------------------------------------------

/** A text field that names someone, or a place, that is or should be
 *  on the client's care team: the dialog draws the care-team combobox
 *  for it (the client's current care providers first, then the
 *  Directory), and a pick travels beside the text as exactly one
 *  registry uuid field. Typing a name stays possible. */
export interface RecordCareTeamField {
  /** The text field the combobox fills. */
  field: string;
  /** Filled by a care-team pick (client_providers.id). Absent where the
   *  record keeps only a Directory organization (a hospital stay): the
   *  care-team row then sends the organization it links, if any. */
  providerIdField?: string;
  /** Filled by a Directory person pick. */
  individualField?: string;
  /** Filled by a Directory organization pick. */
  orgField: string;
  /** The listbox's accessible name. */
  listLabel: string;
  /** Directory organizations only, and care-team rows linked to one. */
  only?: 'organization';
  /** The name case a typed name takes ('person' when absent). */
  nameKind?: NameCaseKind;
}

export const RECORD_CARE_TEAM_FIELDS: Readonly<Partial<Record<RecordFormKey, RecordCareTeamField>>> = {
  medications: {
    field: 'prescriber',
    providerIdField: 'prescriber_provider_id',
    individualField: 'prescriber_directory_individual_id',
    orgField: 'prescriber_directory_org_id',
    listLabel: 'Prescriber choices',
  },
  appointments: {
    field: 'provider',
    providerIdField: 'provider_id',
    individualField: 'provider_directory_individual_id',
    orgField: 'provider_directory_org_id',
    listLabel: 'Provider choices',
  },
  'hospital-events': {
    field: 'facility',
    orgField: 'facility_org_id',
    listLabel: 'Hospital or rehab choices',
    only: 'organization',
    nameKind: 'place',
  },
};

/** The care-team field of a projection, when `field` is it. */
export function careTeamFieldOf(projection: RecordFormKey, field: string): RecordCareTeamField | undefined {
  const entry = RECORD_CARE_TEAM_FIELDS[projection];
  return entry !== undefined && entry.field === field ? entry : undefined;
}

/** The type of place on an address (0225), as the address form and the
 *  card read it, in the form's order. */
export const PLACE_TYPE_WORDS: Readonly<Record<string, string>> = {
  home: 'Home',
  apartment: 'Apartment',
  with_family: 'With family',
  independent_living: 'Independent living',
  group_home: 'Group home',
  assisted_living: 'Assisted living',
  memory_care: 'Memory care',
  nursing_home: 'Nursing home',
  rehab_facility: 'Rehab',
  hospital: 'Hospital',
  hospice: 'Hospice',
  mailing: 'Mailing address only',
  other: 'Other',
};

